import { useState, useEffect, useMemo } from 'react';
import { 
  Button, 
  Typography, 
  Grid, 
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Box,
  Paper,
  TablePagination, 
  TableFooter,
  Chip,
  Card,
  CardContent,
  Skeleton,
  Fade,
  Slide,
  InputAdornment,
  Alert,
  Snackbar
} from '@mui/material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  GetApp as ExportIcon,
  TrendingUp as TrendingUpIcon,
  AttachMoney as MoneyIcon,
  Event as EventIcon,
  Schedule as ScheduleIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import admin_api from '../../../api/admin';
import useCollapseDrawer from '../../../hooks/useCollapseDrawer';
import ExportDialog from './export/ExportDialog';

// Styled components with dark theme
const StyledContainer = styled(Box)(({ theme }) => ({
  backgroundColor: '#12192c',
  minHeight: 'calc(100vh - 100px)',
  padding: '20px',
  color: '#ffffff',
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  backgroundColor: '#1a2235',
  color: '#ffffff',
  borderRadius: theme.spacing(2),
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
  border: '1px solid #2d3748',
}));

const StyledCard = styled(Card)(({ theme }) => ({
  backgroundColor: '#1e293b',
  color: '#ffffff',
  borderRadius: theme.spacing(2),
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
  border: '1px solid #2d3748',
  height: '100%',
}));

const HeaderCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
  color: 'white',
  borderRadius: theme.spacing(2),
  marginBottom: theme.spacing(4),
  boxShadow: '0 10px 30px rgba(6, 182, 212, 0.3)',
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderRadius: theme.spacing(1),
    '& fieldset': {
      borderColor: '#2d3748',
    },
    '&:hover fieldset': {
      borderColor: '#4a5568',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#06b6d4',
    },
  },
  '& .MuiInputLabel-root': {
    color: '#a0aec0',
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#06b6d4',
  },
  '& .MuiOutlinedInput-input': {
    color: '#e2e8f0',
  },
  '& .MuiInputAdornment-root .MuiSvgIcon-root': {
    color: '#06b6d4',
  },
}));

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  '& .MuiInputLabel-root': {
    color: '#a0aec0',
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#06b6d4',
  },
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    '& fieldset': {
      borderColor: '#2d3748',
    },
    '&:hover fieldset': {
      borderColor: '#4a5568',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#06b6d4',
    },
  },
  '& .MuiSelect-select': {
    color: '#e2e8f0',
  },
  '& .MuiSvgIcon-root': {
    color: '#a0aec0',
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
  color: 'white',
  borderRadius: theme.spacing(3),
  textTransform: 'none',
  fontWeight: 500,
  boxShadow: '0 4px 15px rgba(6, 182, 212, 0.3)',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)',
    boxShadow: '0 6px 20px rgba(6, 182, 212, 0.4)',
    transform: 'translateY(-2px)',
  },
  '&:disabled': {
    background: theme.palette.grey[700],
    boxShadow: 'none',
  },
}));

export default function Ledger() {
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const { isCollapse } = useCollapseDrawer();
  const [orderBy, setOrderBy] = useState('bookingId');
  const [order, setOrder] = useState('asc');
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Filter states
  const [selectedCourt, setSelectedCourt] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState('all');
  
  const DRAWER_WIDTH = 280;
  const COLLAPSE_WIDTH = 102;

  const containerStyle = {
    marginTop: '80px',
    marginLeft: isCollapse ? `${COLLAPSE_WIDTH + 24}px` : `${DRAWER_WIDTH + 24}px`,
    marginRight: '24px',
    transition: 'all 0.3s ease',
  };

  if (window.innerWidth <= 1024) {
    containerStyle.marginLeft = '24px';
  }

  const headCells = [
    { id: 'bookingId', label: 'Booking ID' },
    { id: 'courtName', label: 'Court Name' },
    { id: 'reservationDate', label: 'Date' },
    { id: 'timeFrom', label: 'Time' },
    { id: 'hours', label: 'Hours', sortable: false },
    { id: 'amountToPay', label: 'Payment' },
    { id: 'status', label: 'Status', sortable: false }
  ];

  function formatDate(dateString) {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      timeZone: 'Asia/Manila',
      year: 'numeric',
      month: 'short',
      day: '2-digit'
    }).format(date);
  }

  function convertTo12Hour(timeString) {
    if (!timeString) return '';
    
    if (timeString.includes('AM') || timeString.includes('PM')) {
      return timeString;
    }
    
    const [hours, minutes] = timeString.split(':').map(Number);
    
    if (isNaN(hours) || isNaN(minutes)) {
      return timeString;
    }
    
    const period = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours % 12 || 12;
    
    return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
  }

  function calculateTotalHours(timeFrom, timeTo) {
    if (!timeFrom || !timeTo) return 0;
    
    const [fromHours, fromMinutes] = timeFrom.split(":").map(Number);
    const [toHours, toMinutes] = timeTo.split(":").map(Number);
  
    const from = new Date(0, 0, 0, fromHours, fromMinutes);
    const to = new Date(0, 0, 0, toHours, toMinutes);
  
    const diff = (to - from) / (1000 * 60 * 60);
    return diff.toFixed(2);
  }

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await admin_api.get_transactions();
      
      if (response && response.data && response.data.data) {
        const transactionData = response.data.data;
        
        const formattedTransactions = Array.isArray(transactionData) ? transactionData.map(t => ({
          id: t.id || t._id,
          bookingId: t.booking_id || t.bookingId,
          courtName: t.court_name || t.courtName,
          courtLocation: t.court_location || t.courtLocation,
          reservationDate: t.reservation_date || t.reservationDate,
          timeFrom: t.time_from || t.timeFrom,
          timeTo: t.time_to || t.timeTo,
          totalHours: t.total_hours || t.totalHours,
          amountToPay: t.total_amount || t.totalAmount || t.amountToPay || 0,
          status: t.status || t.payment_status || 'completed'
        })) : [];
        
        setTransactions(formattedTransactions);
        setFilteredTransactions(formattedTransactions);
        setSuccessMessage('Transactions loaded successfully');
      } else {
        setTransactions([]);
        setFilteredTransactions([]);
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
      setError('Failed to fetch transactions. Please try again.');
      setTransactions([]);
      setFilteredTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedTransactions = useMemo(() => {
    if (!filteredTransactions || !Array.isArray(filteredTransactions)) {
      return [];
    }
    
    return [...filteredTransactions].sort((a, b) => {
      let aValue = a[orderBy];
      let bValue = b[orderBy];

      if (orderBy === 'timeFrom') {
        aValue = new Date(`1970/01/01 ${a.timeFrom}`).getTime();
        bValue = new Date(`1970/01/01 ${b.timeFrom}`).getTime();
      } else if (orderBy === 'amountToPay') {
        aValue = parseFloat(a.amountToPay);
        bValue = parseFloat(b.amountToPay);
      } else if (orderBy === 'reservationDate') {
        aValue = new Date(a.reservationDate).getTime();
        bValue = new Date(b.reservationDate).getTime();
      }

      if (order === 'desc') {
        return bValue < aValue ? -1 : bValue > aValue ? 1 : 0;
      }
      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    });
  }, [filteredTransactions, orderBy, order]);

  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    if (!Array.isArray(filteredTransactions) || filteredTransactions.length === 0) {
      return {
        totalRevenue: '0.00',
        totalBookings: 0,
        avgDuration: '0.00',
        mostPopularCourt: 'N/A',
        avgBookingValue: '0.00',
        todaysBookings: 0
      };
    }

    try {
      const todayInPH = new Date().toLocaleString('en-US', {timeZone: 'Asia/Manila'}).split(',')[0];
      const today = new Date(todayInPH);

      const totalRevenue = filteredTransactions.reduce((sum, t) => sum + parseFloat(t.amountToPay || 0), 0);
      
      const todaysBookings = filteredTransactions.filter(p => {
        const reservationDate = p.reservationDate || p.reservation_date;
        if (!reservationDate) return false;
        
        const transactionDate = new Date(reservationDate);
        const phDate = new Date(transactionDate.toLocaleString('en-US', {timeZone: 'Asia/Manila'}));
        
        return phDate.getDate() === today.getDate() && 
               phDate.getMonth() === today.getMonth() && 
               phDate.getFullYear() === today.getFullYear();
      }).length;
      
      const courtBookings = filteredTransactions.reduce((acc, t) => {
        const courtName = t.courtName || 'Unknown';
        acc[courtName] = (acc[courtName] || 0) + 1;
        return acc;
      }, {});
      
      const courtEntries = Object.entries(courtBookings);
      const mostBookedCourt = courtEntries.length > 0 ? 
        courtEntries.sort((a, b) => b[1] - a[1])[0] : 
        ['N/A', 0];
      
      const avgDuration = filteredTransactions.reduce((sum, t) => 
        sum + parseFloat(calculateTotalHours(t.timeFrom, t.timeTo)), 0) / filteredTransactions.length;

      return {
        totalRevenue: totalRevenue.toFixed(2),
        totalBookings: filteredTransactions.length,
        todaysBookings: todaysBookings,
        avgDuration: avgDuration.toFixed(2),
        mostPopularCourt: mostBookedCourt[0],
        avgBookingValue: (totalRevenue / filteredTransactions.length).toFixed(2)
      };
    } catch (error) {
      console.error('Error calculating summary stats:', error);
      return {
        totalRevenue: '0.00',
        totalBookings: 0,
        todaysBookings: 0,
        avgDuration: '0.00',
        mostPopularCourt: 'N/A',
        avgBookingValue: '0.00'
      };
    }
  }, [filteredTransactions]);

  // Filter transactions
  useEffect(() => {
    if (!Array.isArray(transactions)) {
      setFilteredTransactions([]);
      return;
    }
    
    let filtered = [...transactions];
    
    // Court filter
    if (selectedCourt !== 'all') {
      filtered = filtered.filter(t => t.courtName === selectedCourt);
    }
    
    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(t => 
        (t.bookingId && t.bookingId.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (t.courtName && t.courtName.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }
    
    // Date range filter
    if (dateRange !== 'all') {
      const now = new Date();
      const today = new Date(now.toLocaleString('en-US', {timeZone: 'Asia/Manila'}));
      
      filtered = filtered.filter(t => {
        if (!t.reservationDate) return false;
        
        const transactionDate = new Date(t.reservationDate);
        const phDate = new Date(transactionDate.toLocaleString('en-US', {timeZone: 'Asia/Manila'}));
        
        switch (dateRange) {
          case 'today':
            return phDate.getDate() === today.getDate() && 
                   phDate.getMonth() === today.getMonth() && 
                   phDate.getFullYear() === today.getFullYear();
          case 'week':
            const weekAgo = new Date(today);
            weekAgo.setDate(today.getDate() - 7);
            return phDate >= weekAgo && phDate <= today;
          case 'month':
            return phDate.getMonth() === today.getMonth() && 
                   phDate.getFullYear() === today.getFullYear();
          default:
            return true;
        }
      });
    }
    
    setFilteredTransactions(filtered);
    setPage(0); // Reset pagination when filters change
  }, [transactions, selectedCourt, searchQuery, dateRange]);

  // Get unique courts for filter dropdown
  const courts = useMemo(() => {
    if (!Array.isArray(transactions)) return [];
    
    const courtNames = transactions
      .map(t => t.courtName)
      .filter(Boolean);
      
    return [...new Set(courtNames)];
  }, [transactions]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Get current page data
  const paginatedTransactions = useMemo(() => {
    if (!Array.isArray(sortedTransactions)) return [];
    
    return sortedTransactions.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  }, [page, rowsPerPage, sortedTransactions]);

  useEffect(() => {
    fetchTransactions();
    
    return () => {
      // Cleanup function to prevent state updates on unmounted component
    };
  }, []);

  // Clear messages after 5 seconds
  useEffect(() => {
    if (successMessage || error) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
        setError(null);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [successMessage, error]);

  return (
    <StyledContainer sx={containerStyle}>
      {/* Success Message */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={5000}
        onClose={() => setSuccessMessage('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" variant="filled" sx={{ backgroundColor: '#22c55e' }}>
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Error Message */}
      <Snackbar
        open={!!error}
        autoHideDuration={5000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" variant="filled" sx={{ backgroundColor: '#ef4444' }}>
          {error}
        </Alert>
      </Snackbar>

      <Fade in timeout={800}>
        <Box>
          {/* Header */}
          <HeaderCard>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                Transaction Ledger
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9 }}>
                View and analyze all court booking transactions
              </Typography>
            </CardContent>
          </HeaderCard>

          {/* Summary Statistics Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Slide direction="up" in timeout={1000}>
                <StyledCard>
                  <CardContent sx={{ textAlign: 'center', p: 3 }}>
                    <MoneyIcon sx={{ fontSize: 40, color: '#22c55e', mb: 2 }} />
                    <Typography variant="h6" gutterBottom sx={{ color: '#e2e8f0' }}>
                      Total Revenue
                    </Typography>
                    <Typography variant="h4" sx={{ color: '#22c55e', fontWeight: 'bold' }}>
                      ₱{summaryStats.totalRevenue}
                    </Typography>
                    <Typography sx={{ color: '#a0aec0', mt: 1, fontSize: '0.875rem' }}>
                      All time earnings
                    </Typography>
                  </CardContent>
                </StyledCard>
              </Slide>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Slide direction="up" in timeout={1200}>
                <StyledCard>
                  <CardContent sx={{ textAlign: 'center', p: 3 }}>
                    <AssessmentIcon sx={{ fontSize: 40, color: '#06b6d4', mb: 2 }} />
                    <Typography variant="h6" gutterBottom sx={{ color: '#e2e8f0' }}>
                      Total Bookings
                    </Typography>
                    <Typography variant="h4" sx={{ color: '#06b6d4', fontWeight: 'bold' }}>
                      {summaryStats.totalBookings}
                    </Typography>
                    <Typography sx={{ color: '#a0aec0', mt: 1, fontSize: '0.875rem' }}>
                      Completed transactions
                    </Typography>
                  </CardContent>
                </StyledCard>
              </Slide>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Slide direction="up" in timeout={1400}>
                <StyledCard>
                  <CardContent sx={{ textAlign: 'center', p: 3 }}>
                    <EventIcon sx={{ fontSize: 40, color: '#f59e0b', mb: 2 }} />
                    <Typography variant="h6" gutterBottom sx={{ color: '#e2e8f0' }}>
                      Today's Bookings
                    </Typography>
                    <Typography variant="h4" sx={{ color: '#f59e0b', fontWeight: 'bold' }}>
                      {summaryStats.todaysBookings}
                    </Typography>
                    <Typography sx={{ color: '#a0aec0', mt: 1, fontSize: '0.875rem' }}>
                      Bookings scheduled today
                    </Typography>
                  </CardContent>
                </StyledCard>
              </Slide>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Slide direction="up" in timeout={1600}>
                <StyledCard>
                  <CardContent sx={{ textAlign: 'center', p: 3 }}>
                    <ScheduleIcon sx={{ fontSize: 40, color: '#8b5cf6', mb: 2 }} />
                    <Typography variant="h6" gutterBottom sx={{ color: '#e2e8f0' }}>
                      Avg. Duration
                    </Typography>
                    <Typography variant="h4" sx={{ color: '#8b5cf6', fontWeight: 'bold' }}>
                      {summaryStats.avgDuration}h
                    </Typography>
                    <Typography sx={{ color: '#a0aec0', mt: 1, fontSize: '0.875rem' }}>
                      Average booking length
                    </Typography>
                  </CardContent>
                </StyledCard>
              </Slide>
            </Grid>
          </Grid>

          {/* Additional Stats Row */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <StyledCard>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ color: '#06b6d4', fontWeight: 'bold' }}>
                    Revenue Analytics
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" sx={{ color: '#a0aec0' }}>
                        Avg. Booking Value
                      </Typography>
                      <Typography variant="h5" sx={{ color: '#22c55e', fontWeight: 'bold' }}>
                        ₱{summaryStats.avgBookingValue}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" sx={{ color: '#a0aec0' }}>
                        Most Popular Court
                      </Typography>
                      <Typography variant="h6" sx={{ color: '#e2e8f0', fontWeight: 'bold' }}>
                        {summaryStats.mostPopularCourt}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </StyledCard>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <StyledCard>
                <CardContent sx={{ p: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h6" gutterBottom sx={{ color: '#06b6d4', fontWeight: 'bold' }}>
                      Quick Actions
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#a0aec0', mb: 2 }}>
                      Export and analyze your transaction data
                    </Typography>
                  </Box>
                  <StyledButton
                    startIcon={<ExportIcon />}
                    onClick={() => setExportDialogOpen(true)}
                    disabled={loading || !Array.isArray(transactions) || transactions.length === 0}
                  >
                    Export Data
                  </StyledButton>
                </CardContent>
              </StyledCard>
            </Grid>
          </Grid>

          {/* Filters */}
          <StyledPaper sx={{ p: 3, mb: 4 }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#06b6d4', fontWeight: 'bold', mb: 3 }}>
              Filter Transactions
            </Typography>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={3}>
                <StyledTextField
                  fullWidth
                  size="small"
                  label="Search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search booking ID or court..."
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={3}>
                <StyledFormControl fullWidth size="small">
                  <InputLabel>Court</InputLabel>
                  <Select
                    value={selectedCourt}
                    label="Court"
                    onChange={(e) => setSelectedCourt(e.target.value)}
                    startAdornment={<FilterIcon sx={{ mr: 1, color: '#06b6d4' }} />}
                  >
                    <MenuItem value="all">All Courts</MenuItem>
                    {Array.isArray(courts) && courts.map((court, index) => (
                      <MenuItem key={index} value={court}>{court}</MenuItem>
                    ))}
                  </Select>
                </StyledFormControl>
              </Grid>
              
              <Grid item xs={12} md={3}>
                <StyledFormControl fullWidth size="small">
                  <InputLabel>Date Range</InputLabel>
                  <Select
                    value={dateRange}
                    label="Date Range"
                    onChange={(e) => setDateRange(e.target.value)}
                  >
                    <MenuItem value="all">All Time</MenuItem>
                    <MenuItem value="today">Today</MenuItem>
                    <MenuItem value="week">This Week</MenuItem>
                    <MenuItem value="month">This Month</MenuItem>
                  </Select>
                </StyledFormControl>
              </Grid>
              
              <Grid item xs={12} md={3}>
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={fetchTransactions}
                    disabled={loading}
                    sx={{
                      color: '#e2e8f0',
                      borderColor: '#4a5568',
                      '&:hover': { 
                        borderColor: '#06b6d4', 
                        backgroundColor: 'rgba(6, 182, 212, 0.1)' 
                      }
                    }}
                  >
                    Refresh
                  </Button>
                  <Typography 
                    variant="body2" 
                    sx={{ color: '#a0aec0', alignSelf: 'center', ml: 1 }}
                  >
                    {filteredTransactions.length} record{filteredTransactions.length !== 1 ? 's' : ''}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </StyledPaper>
          
          {/* Table */}
          <Slide direction="up" in timeout={1800}>
            <TableContainer component={StyledPaper} sx={{ overflowX: 'auto' }}>
              {loading ? (
                <Box sx={{ p: 4 }}>
                  {[...Array(5)].map((_, index) => (
                    <Box key={index} sx={{ display: 'flex', gap: 2, mb: 2 }}>
                      <Skeleton variant="text" width="15%" height={40} sx={{ bgcolor: '#2d3748' }} />
                      <Skeleton variant="text" width="20%" height={40} sx={{ bgcolor: '#2d3748' }} />
                      <Skeleton variant="text" width="15%" height={40} sx={{ bgcolor: '#2d3748' }} />
                      <Skeleton variant="text" width="20%" height={40} sx={{ bgcolor: '#2d3748' }} />
                      <Skeleton variant="text" width="10%" height={40} sx={{ bgcolor: '#2d3748' }} />
                      <Skeleton variant="text" width="15%" height={40} sx={{ bgcolor: '#2d3748' }} />
                      <Skeleton variant="rectangular" width="5%" height={30} sx={{ bgcolor: '#2d3748' }} />
                    </Box>
                  ))}
                </Box>
              ) : error ? (
                <Box sx={{ p: 6, textAlign: 'center' }}>
                  <AssessmentIcon sx={{ fontSize: 60, color: '#ef4444', mb: 2 }} />
                  <Typography variant="h6" sx={{ color: '#ef4444', mb: 2 }}>
                    {error}
                  </Typography>
                  <StyledButton onClick={fetchTransactions}>
                    Try Again
                  </StyledButton>
                </Box>
              ) : !Array.isArray(filteredTransactions) || filteredTransactions.length === 0 ? (
                <Box sx={{ p: 6, textAlign: 'center' }}>
                  <AssessmentIcon sx={{ fontSize: 60, color: '#4a5568', mb: 2 }} />
                  <Typography variant="h6" sx={{ color: '#e2e8f0', mb: 1 }}>
                    No transactions found
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#a0aec0', mb: 3 }}>
                    {searchQuery || selectedCourt !== 'all' || dateRange !== 'all' 
                      ? 'No transactions match your current filters. Try adjusting your search criteria.'
                      : 'No transactions have been recorded yet. Transactions will appear here once bookings are made.'}
                  </Typography>
                  {(searchQuery || selectedCourt !== 'all' || dateRange !== 'all') && (
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setSearchQuery('');
                        setSelectedCourt('all');
                        setDateRange('all');
                      }}
                      sx={{
                        color: '#e2e8f0',
                        borderColor: '#4a5568',
                        '&:hover': { 
                          borderColor: '#06b6d4', 
                          backgroundColor: 'rgba(6, 182, 212, 0.1)' 
                        }
                      }}
                    >
                      Clear Filters
                    </Button>
                  )}
                </Box>
              ) : (
                <Table sx={{ minWidth: 650 }} aria-label="enhanced transactions table">
                  <TableHead>
                    <TableRow sx={{ 
                      '& .MuiTableCell-head': { 
                        backgroundColor: '#1e293b', 
                        color: '#06b6d4', 
                        fontWeight: 'bold',
                        borderBottom: '2px solid #2d3748'
                      } 
                    }}>
                      {headCells.map((headCell) => (
                        <TableCell
                          key={headCell.id}
                          align={headCell.numeric ? 'right' : 'left'}
                          sortDirection={orderBy === headCell.id ? order : false}
                        >
                          {headCell.sortable !== false ? (
                            <TableSortLabel
                              active={orderBy === headCell.id}
                              direction={orderBy === headCell.id ? order : 'asc'}
                              onClick={() => handleRequestSort(headCell.id)}
                              sx={{
                                color: '#06b6d4 !important',
                                '& .MuiTableSortLabel-icon': {
                                  color: '#06b6d4 !important',
                                },
                              }}
                            >
                              {headCell.label}
                            </TableSortLabel>
                          ) : (
                            headCell.label
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedTransactions.map((transaction, index) => (
                      <Fade in timeout={300 + index * 100} key={transaction.id || transaction._id || index}>
                        <TableRow 
                          sx={{ 
                            '& .MuiTableCell-root': { 
                              color: '#e2e8f0',
                              borderColor: '#2d3748'
                            },
                            '&:hover': {
                              backgroundColor: 'rgba(6, 182, 212, 0.05)'
                            }
                          }}
                        >
                          <TableCell component="th" scope="row">
                            <Chip 
                              label={transaction.bookingId || transaction.booking_id || '-'} 
                              size="small"
                              sx={{
                                backgroundColor: 'rgba(6, 182, 212, 0.1)',
                                color: '#06b6d4',
                                fontWeight: 'bold'
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              {transaction.courtName || transaction.court_name || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {formatDate(transaction.reservationDate || transaction.reservation_date)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {`${convertTo12Hour(transaction.timeFrom || transaction.time_from || '-')} - ${convertTo12Hour(transaction.timeTo || transaction.time_to || '-')}`}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={`${calculateTotalHours(
                                transaction.timeFrom || transaction.time_from, 
                                transaction.timeTo || transaction.time_to
                              )}h`}
                              size="small"
                              sx={{
                                backgroundColor: 'rgba(139, 92, 246, 0.1)',
                                color: '#8b5cf6'
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body1" sx={{ color: '#22c55e', fontWeight: 'bold' }}>
                              ₱{parseFloat(transaction.amountToPay || transaction.total_amount || 0).toLocaleString()}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={transaction.status || transaction.payment_status || 'Completed'} 
                              color={
                                (transaction.status || transaction.payment_status || '').toLowerCase() === 'pending' ? 'warning' :
                                (transaction.status || transaction.payment_status || '').toLowerCase() === 'rejected' ? 'error' :
                                'success'
                              }
                              size="small"
                              sx={{
                                ...(((transaction.status || transaction.payment_status || '').toLowerCase() === 'pending') && {
                                  backgroundColor: 'rgba(245, 158, 11, 0.1)',
                                  color: '#f59e0b'
                                }),
                                ...(((transaction.status || transaction.payment_status || '').toLowerCase() === 'rejected') && {
                                  backgroundColor: 'rgba(239, 68, 68, 0.1)',
                                  color: '#ef4444'
                                }),
                                ...(!['pending', 'rejected'].includes((transaction.status || transaction.payment_status || '').toLowerCase()) && {
                                  backgroundColor: 'rgba(34, 197, 94, 0.1)',
                                  color: '#22c55e'
                                })
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      </Fade>
                    ))}
                  </TableBody>
                  <TableFooter>
                    <TableRow>
                      <TablePagination
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        colSpan={headCells.length}
                        count={filteredTransactions.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        sx={{
                          color: '#e2e8f0',
                          borderTop: '1px solid #2d3748',
                          '& .MuiTablePagination-selectIcon': { color: '#e2e8f0' },
                          '& .MuiTablePagination-actions button': { color: '#e2e8f0' }
                        }}
                      />
                    </TableRow>
                  </TableFooter>
                </Table>
              )}
            </TableContainer>
          </Slide>
        </Box>
      </Fade>

      <ExportDialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        currentTransactions={transactions}
      />
    </StyledContainer>
  );
}